import { useState } from 'react';
import React from 'react';
import { Tabs, Badge, Input } from 'antd';
import Sider from 'antd/es/layout/Sider';
import { CloseOutlined } from '@ant-design/icons';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import { TAB_KEYS } from './constants';
import { useNotifications } from './hooks/useNotifications';
import { useNotificationSearch } from './hooks/useNotificationSearch';
import NotificationCardFactory from './components/NotificationCardFactory';
import type { TabKey } from './types';

export default function Notifications() {
	const {
		context,
		setValue,
		activeTab,
		setActiveTab,
		tabCounts,
		currentState,
		byTab,
		loadMore,
		dismissNotification,
		showConfirmDismiss,
		setShowConfirmDismiss,
		selectedNotification,
		setSelectedNotification,
	} = useNotifications();

	const [search, setSearch] = useState('');
	const filtered = useNotificationSearch(byTab, search);
	const renderableData = filtered[activeTab];

	const [sentryRef, { rootRef }] = useInfiniteScroll({
		loading: currentState.loading,
		hasNextPage: currentState.hasMore,
		onLoadMore: loadMore,
		rootMargin: '0px 0px 200px 0px',
	});

	return (
		<Sider
			width={500}
			style={{
				backgroundColor: '#fff',
				height: '100vh',
				position: 'absolute',
				right: 0,
				top: 0,
				zIndex: 10,
			}}
		>
			<div className="flex column" style={{ padding: 10 }}>
				<div style={{ position: 'relative', height: '100%' }}>
					<CloseOutlined
						style={{
							position: 'absolute',
							right: 20,
							top: 12,
							fontSize: 16,
							cursor: 'pointer',
							fontWeight: 700,
							zIndex: 10000,
							padding: 4,
							color: '#000',
							borderRadius: 10,
						}}
						onClick={() => setValue({ ...context, showNotifications: false })}
					/>
				</div>

				<Tabs
					activeKey={activeTab}
					onChange={(key) => setActiveTab(key as TabKey)}
					items={[
						{
							key: TAB_KEYS.ALL,
							label: (
								<span>
									All{' '}
									<Badge count={tabCounts[TAB_KEYS.ALL]} offset={[4, -2]} style={{ background: '#006fbf' }} />
								</span>
							),
						},
						{
							key: TAB_KEYS.SYSTEM,
							label: (
								<span>
									ONLY ALERTS{' '}
									<Badge count={tabCounts[TAB_KEYS.SYSTEM]} offset={[4, -2]} style={{ background: '#B51622' }} />
								</span>
							),
						},
						{
							key: TAB_KEYS.FORECAST,
							label: (
								<span>
									STRATEGIES{' '}
									<Badge count={tabCounts[TAB_KEYS.FORECAST]} offset={[4, -2]} style={{ background: '#2586e4' }} />
								</span>
							),
						},
					]}
					style={{ marginBottom: 8 }}
				/>

				<Input allowClear placeholder="Search by asset, sector, issue, type…" value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" />

				<div
					ref={rootRef}
					className="flex column items-center"
					style={{
						width: 480,
						padding: '0 5px',
						height: 'calc(100vh - 170px)',
						overflowX: 'hidden',
						overflowY: 'scroll',
					}}
				>
					{renderableData.map((act, idx) => (
						<NotificationCardFactory
							key={act.dispatchId || idx}
							act={{
								...act,
								onDismiss: act.dispatchId
									? () => {
											setSelectedNotification(String(act.dispatchId));
											setShowConfirmDismiss(true);
										}
									: undefined,
							}}
						/>
					))}

					{(currentState.loading || currentState.hasMore) && (
						<div ref={sentryRef} style={{ textAlign: 'center', padding: 20 }}>
							{currentState.loading ? 'Loading more data...' : ''}
						</div>
					)}
				</div>
			</div>

			<ConfirmationModal
				title="Dismiss notification"
				message="Are you sure you want to dismiss this notification?"
				showModal={showConfirmDismiss}
				onAccept={async () => {
					if (selectedNotification) await dismissNotification(selectedNotification);
					setShowConfirmDismiss(false);
				}}
				oncancel={() => setShowConfirmDismiss(false)}
			/>
		</Sider>
	);
}
